import type { TFunction } from 'i18next'
import type { Trip } from '@/types'

export const cn = (...classes: (string | boolean | undefined | null)[]) =>
  classes.filter(Boolean).join(' ')

/** Promise that resolves after `ms`, or early when `signal` aborts. */
export function delay(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (signal?.aborted) { resolve(); return }
    const onAbort = () => { clearTimeout(id); resolve() }
    const id = setTimeout(() => { signal?.removeEventListener('abort', onAbort); resolve() }, ms)
    signal?.addEventListener('abort', onAbort, { once: true })
  })
}

/** Shape returned by Firebase Admin's serializer for Firestore Timestamps. */
export type FirestoreTimestampLike = { _seconds: number; _nanoseconds?: number }

/** Coerce a Firestore-serialized timestamp or ISO string to Date. Returns null on missing/invalid input. */
export function parseFirestoreTimestamp(
  ts: FirestoreTimestampLike | string | null | undefined,
): Date | null {
  if (!ts) return null
  if (typeof ts === 'string') {
    const d = new Date(ts)
    return Number.isFinite(d.getTime()) ? d : null
  }
  if (typeof ts === 'object' && '_seconds' in ts) {
    return new Date(ts._seconds * 1000)
  }
  return null
}

/** UTC `YYYY-MM-DD` day key for today — the backend's `todayKey()`. */
export function todayKey(): string {
  return dayKeyOf(new Date())
}

/** UTC `YYYY-MM-DD` day key for `d` — the backend's `dayKeyOf(d)`. */
export function dayKeyOf(d: Date): string {
  return d.toISOString().slice(0, 10)
}

/** Day key (YYYY-MM-DD, UTC) for `days` ago from today. */
export function daysAgoKey(days: number): string {
  return new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString().slice(0, 10)
}

/** True once a trip's proposed window has fully passed (end date before today, UTC). */
export function isTripPast(trip: Pick<Trip, 'endDate'>): boolean {
  return !!trip.endDate && trip.endDate.slice(0, 10) < todayKey()
}

/**
 * Should a trip whose dates have passed get the "How was it?" prompt — the
 * entry point to rating and the trip journal?
 *
 * - completed/confirmed/active always qualify: the trip was explicitly on.
 *   `completed` is load-bearing — deriveStatus() already maps an ended
 *   confirmed/active trip to 'completed', so a confirmed/active-only test is
 *   false for essentially every trip that has actually ended.
 * - a `planning` trip qualifies when it has real itinerary content (at least
 *   one day with at least one activity) AND its plan is not still being
 *   written. Status only ever changes by explicit user action and most users
 *   never touch it, so demanding an explicit status silently loses the recap
 *   entry point for the majority — while the C8 recap email keeps sending.
 *   The content + plan-state conditions still exclude the case the gate was
 *   built for: a past-dated trip that is mid-generation.
 * - `dreaming` never qualifies (it was an idea, not a trip), nor `cancelled`.
 *
 * Caller supplies the effective status so an optimistic status change is
 * honored without waiting for the snapshot.
 */
export function isCompletionPromptEligible(
  effectiveStatus: Trip['status'],
  trip: Pick<Trip, 'planStatus'>,
  days: Array<{ activities: unknown[] }>,
): boolean {
  if (effectiveStatus === 'completed' || effectiveStatus === 'confirmed' || effectiveStatus === 'active') {
    return true
  }
  if (effectiveStatus !== 'planning') return false
  const planStillWriting = trip.planStatus === 'generating' || trip.planStatus === 'enriching'
  return !planStillWriting && days.some((day) => day.activities.length > 0)
}

/**
 * Comparator for trip lists: soonest upcoming start date first, undated trips
 * last, same-day ties broken by higher vote count. startDate is a YYYY-MM-DD
 * string, so a lexical comparison is chronological. Pass to `[...].sort()`;
 * use `(a, b) => byUpcomingTrip(b, a)` for a most-recent-first archive order.
 */
export function byUpcomingTrip(
  a: Pick<Trip, 'startDate' | 'voteCount'>,
  b: Pick<Trip, 'startDate' | 'voteCount'>,
): number {
  const aDate = a.startDate || ''
  const bDate = b.startDate || ''
  if (aDate !== bDate) {
    if (!aDate) return 1
    if (!bDate) return -1
    return aDate < bDate ? -1 : 1
  }
  return (b.voteCount ?? 0) - (a.voteCount ?? 0)
}

export function formatDate(date: string | null | undefined): string {
  if (!date) return ''
  // Treat YYYY-MM-DD as local date (not UTC) to avoid off-by-one
  const d = date.length === 10 ? new Date(date + 'T12:00:00') : new Date(date)
  if (isNaN(d.getTime())) return ''
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

export function formatDateRange(start: string | null | undefined, end: string | null | undefined): string {
  if (!start && !end) return ''
  if (!start) return formatDate(end)
  if (!end) return formatDate(start)
  const s = start.length === 10 ? new Date(start + 'T12:00:00') : new Date(start)
  const e = end.length === 10 ? new Date(end + 'T12:00:00') : new Date(end)
  if (isNaN(s.getTime()) || isNaN(e.getTime())) return `${formatDate(start)} – ${formatDate(end)}`
  if (s.getFullYear() === e.getFullYear() && s.getMonth() === e.getMonth()) {
    // Same month: "Mar 9–15, 2026"
    return `${s.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}–${e.getDate()}, ${e.getFullYear()}`
  }
  if (s.getFullYear() === e.getFullYear()) {
    // Same year: "Mar 9 – Apr 2, 2026"
    return `${s.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${e.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}, ${e.getFullYear()}`
  }
  return `${formatDate(start)} – ${formatDate(end)}`
}

export function relativeTime(date: Date, t?: TFunction): string {
  const now = new Date()
  const diff = now.getTime() - date.getTime()
  const seconds = Math.floor(diff / 1000)
  const minutes = Math.floor(seconds / 60)
  const hours = Math.floor(minutes / 60)
  const days = Math.floor(hours / 24)

  if (seconds < 60) return t ? t('common.timeAgo.justNow', 'just now') : 'just now'
  if (minutes < 60) return t ? t('common.timeAgo.minutesAgo', '{{count}}m ago', { count: minutes }) : `${minutes}m ago`
  if (hours < 24) return t ? t('common.timeAgo.hoursAgo', '{{count}}h ago', { count: hours }) : `${hours}h ago`
  if (days < 7) return t ? t('common.timeAgo.daysAgo', '{{count}}d ago', { count: days }) : `${days}d ago`
  return formatDate(date.toISOString())
}

export function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

export function formatCurrency(amount: number, currency: string = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount)
}

/**
 * Format integer USD cents as a 2-decimal dollar string with thousands
 * separators ("$1,234.56", "$200.00", "-$5.00"). Fractional cents (e.g.
 * Foursquare's 0.05¢/call rollups) are rounded by Intl, so callers can pass
 * the raw FieldValue.increment accumulator without pre-rounding.
 *
 * Use this for cents-denominated counters across the admin panels — Stripe
 * revenue, AI costs, places spend, FSQ credit burn — so they all read the
 * same.
 */
export function formatUsdCents(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(cents / 100)
}

/**
 * Format a USD amount given in DOLLARS ("$1,234.56", "$0.004"). Sub-dollar
 * amounts (a run's AI cost, a cost per click) keep three decimals so a real
 * $0.004 does not read as $0.00; pass `fractionDigits` to fix the precision.
 * Null/undefined/non-finite → "—" (not measured), never "$0.00".
 *
 * Use `formatUsdCents` for cents-denominated counters.
 */
export function formatUsd(dollars: number | null | undefined, fractionDigits?: number): string {
  if (dollars == null || !Number.isFinite(dollars)) return '—'
  const digits = fractionDigits ?? (Math.abs(dollars) < 1 ? 3 : 2)
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(dollars)
}

/**
 * "1.2 MB" / "820 kB" in the viewer's locale — the unit comes from
 * `Intl.NumberFormat`, never a hardcoded English "KB". Binary units (1024).
 */
export function formatBytes(bytes: number, locale: string | undefined): string {
  const [unit, value, maximumFractionDigits] =
    bytes < 1024 ? ['byte', bytes, 0] as const
      : bytes < 1024 * 1024 ? ['kilobyte', bytes / 1024, 0] as const
        : ['megabyte', bytes / (1024 * 1024), 1] as const
  return new Intl.NumberFormat(locale, { style: 'unit', unit, unitDisplay: 'short', maximumFractionDigits }).format(value)
}

/** Read a File as a base64 string (no `data:...;base64,` prefix). */
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = reader.result
      if (typeof result !== 'string') {
        reject(new Error('Unexpected reader result'))
        return
      }
      const comma = result.indexOf(',')
      resolve(comma >= 0 ? result.slice(comma + 1) : result)
    }
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read file'))
    reader.readAsDataURL(file)
  })
}

/** Host part of a URL, or `'invalid'` if parsing fails. */
export function urlHost(url: string): string {
  try { return new URL(url).host } catch { return 'invalid' }
}

/**
 * Load a File into an ImageBitmap-like source for canvas drawing.
 * Prefers createImageBitmap (fast, off-main-thread decode) with a
 * fallback to HTMLImageElement + object URL for older browsers.
 */
async function loadDrawableImage(
  file: File,
): Promise<{
  source: CanvasImageSource
  width: number
  height: number
  cleanup: () => void
}> {
  if (typeof createImageBitmap === 'function') {
    const bitmap = await createImageBitmap(file)
    return {
      source: bitmap,
      width: bitmap.width,
      height: bitmap.height,
      cleanup: () => {
        if ('close' in bitmap && typeof bitmap.close === 'function') bitmap.close()
      },
    }
  }
  const url = URL.createObjectURL(file)
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image()
      el.onload = () => resolve(el)
      el.onerror = () => reject(new Error('Failed to decode image'))
      el.src = url
    })
    return {
      source: img,
      width: img.naturalWidth,
      height: img.naturalHeight,
      cleanup: () => URL.revokeObjectURL(url),
    }
  } catch (err) {
    URL.revokeObjectURL(url)
    throw err
  }
}

/**
 * Resize an image File and return JPEG base64 ready for upload.
 *
 * - Never upscales: if both dims <= maxDim, keeps original size.
 * - Always outputs JPEG (alpha stripped — fine for cover photos).
 * - Returns raw base64 (no `data:` prefix) to match the upload API, plus the
 *   encoded pixel size so callers can reserve the right aspect ratio.
 */
export async function resizeImageToBase64(
  file: File,
  opts?: { maxDim?: number; quality?: number },
): Promise<{ data: string; contentType: string; bytes: number; width: number; height: number }> {
  const maxDim = opts?.maxDim ?? 1920
  const quality = opts?.quality ?? 0.85
  const contentType = 'image/jpeg'

  const { source, width, height, cleanup } = await loadDrawableImage(file)
  try {
    const scale = Math.min(1, maxDim / Math.max(width, height))
    const targetW = Math.max(1, Math.round(width * scale))
    const targetH = Math.max(1, Math.round(height * scale))

    // Prefer OffscreenCanvas when available (avoids touching the DOM).
    let blob: Blob
    if (typeof OffscreenCanvas !== 'undefined') {
      const canvas = new OffscreenCanvas(targetW, targetH)
      const ctx = canvas.getContext('2d')
      if (!ctx) throw new Error('Canvas 2D context unavailable')
      ctx.drawImage(source, 0, 0, targetW, targetH)
      blob = await canvas.convertToBlob({ type: contentType, quality })
    } else {
      const canvas = document.createElement('canvas')
      canvas.width = targetW
      canvas.height = targetH
      const ctx = canvas.getContext('2d')
      if (!ctx) throw new Error('Canvas 2D context unavailable')
      ctx.drawImage(source, 0, 0, targetW, targetH)
      blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(
          (b) => (b ? resolve(b) : reject(new Error('Failed to encode image'))),
          contentType,
          quality,
        )
      })
    }

    const buf = await blob.arrayBuffer()
    // Encode to base64 in chunks to avoid argument length limits on large buffers.
    const bytes = new Uint8Array(buf)
    let binary = ''
    const chunk = 0x8000
    for (let i = 0; i < bytes.length; i += chunk) {
      binary += String.fromCharCode.apply(
        null,
        bytes.subarray(i, i + chunk) as unknown as number[],
      )
    }
    const data = btoa(binary)
    return { data, contentType, bytes: blob.size, width: targetW, height: targetH }
  } finally {
    cleanup()
  }
}
